// /client/customer/src/components/ui/BirthdayPickerEn.tsx
import React, { useEffect, useMemo, useState } from 'react';

type Props = {
  value: string;                 // 'YYYY-MM-DD' or ''
  onChange: (v: string) => void; // 完成時: 'YYYY-MM-DD' / クリア時: ''
  min?: string;                  // 'YYYY-MM-DD' (default: '1900-01-01')
  max?: string;                  // 'YYYY-MM-DD' (例: 今日)
  label?: string;
  className?: string;
  allowClear?: boolean;          // default: true
  ariaLabel?: string;
  disabled?: boolean;
  disableYearOnly?: boolean;
  onYearChange?: (year: number) => void;
};

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
] as const;
const pad2 = (n: number) => String(n).padStart(2, '0');
const range = (from: number, to: number) => {
  const step = from <= to ? 1 : -1;
  const out: number[] = [];
  for (let v = from; step > 0 ? v <= to : v >= to; v += step) out.push(v);
  return out;
};
const daysInMonth = (y: number, m: number) => (y && m ? new Date(y, m, 0).getDate() : 31);

const parseYmd = (s?: string) => {
  const [ys, ms, ds] = (s || '').split('-');
  const y = Number(ys) || 0;
  const m = Number(ms) || 0;
  const d = Number(ds) || 0;
  return { y, m, d, ok: !!(y && m && d) };
};

export default function BirthdayPickerEn({
  value,
  onChange,
  min = '1900-01-01',
  max,
  label,
  className,
  allowClear = true,
  ariaLabel,
  disabled = false,
  disableYearOnly = false,
  onYearChange,
}: Props) {
  // ---- 内部の局所状態（部分選択を保持）----
  const parsed = parseYmd(value);
  const [y, setY] = useState<number>(parsed.y);
  const [m, setM] = useState<number>(parsed.m);
  const [d, setD] = useState<number>(parsed.d);

  // 親 value が外部から変わったら同期
  useEffect(() => {
    const p = parseYmd(value);
    setY(p.y); setM(p.m); setD(p.d);
    if (onYearChange) onYearChange(p.y);
  }, [value]);

  // 年候補（min/max から決定）
  const now = new Date();
  const maxYear = Number((max || `${now.getFullYear()}-12-31`).slice(0, 4)) || now.getFullYear();
  const minYear = Number(min.slice(0, 4)) || 1900;
  const years = useMemo(() => range(maxYear, minYear), [maxYear, minYear]);

  const days = useMemo(() => range(1, daysInMonth(y, m)), [y, m]);

  // 完成時のみ emit（3つ揃ったら）
  const tryEmitIfComplete = (ny: number, nm: number, nd: number) => {
    if (ny && nm && nd) {
      const composed = `${ny}-${pad2(nm)}-${pad2(nd)}`;
      // min/max clamp（越えたら emit せず内部だけ維持）
      if (min && composed < min) return;
      if (max && composed > max) return;
      onChange(composed);
    }
  };

  const onYear = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (disabled || disableYearOnly) return;
    const ny = Number(e.target.value) || 0;
    // 既存 day が新しい年月の日数を超えていたら丸める
    const maxDay = daysInMonth(ny, m || 1);
    const nd = d ? Math.min(d, maxDay) : d;
    setY(ny); setD(nd ?? 0);
    if (onYearChange) onYearChange(ny);
    tryEmitIfComplete(ny, m, nd ?? 0);
  };

  const onMonth = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (disabled) return;
    const nm = Number(e.target.value) || 0;
    const maxDay = daysInMonth(y || 0, nm || 1);
    const nd = d ? Math.min(d, maxDay) : d;
    setM(nm); setD(nd ?? 0);
    tryEmitIfComplete(y, nm, nd ?? 0);
  };

  const onDay = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (disabled) return;
    const nd = Number(e.target.value) || 0;
    setD(nd);
    tryEmitIfComplete(y, m, nd);
  };

  const clearAll = () => {
    if (disabled) return;
    setY(0); setM(0); setD(0);
    if (onYearChange) onYearChange(0);
    onChange(''); // クリアは即反映
  };

  return (
    <div className={className ?? ''} aria-label={ariaLabel || 'Birthday picker'}>
      {label && <div className="mb-1 text-sm font-semibold">{label}</div>}

      <div className="flex flex-wrap items-center gap-2">
        {/* Day */}
        <select
          className="border rounded px-2 py-2 text-sm w-[90px]"
          value={d || ''}             // ★ 局所状態を直接バインド
          onChange={onDay}
          aria-label="Day"
          disabled={disabled}
        >
          <option value="">Day</option>
          {days.map((day) => (
            <option key={day} value={day}>{day}</option>
          ))}
        </select>

        {/* Month */}
        <select
          className="border rounded px-2 py-2 text-sm w-[160px]"
          value={m || ''}             // ★ 局所状態
          onChange={onMonth}
          aria-label="Month"
          disabled={disabled}
        >
          <option value="">Month</option>
          {MONTH_NAMES.map((name, idx) => (
            <option key={name} value={idx + 1}>{name}</option>
          ))}
        </select>

        {/* Year */}
        <select
          className={[
            'border rounded px-2 py-2 text-sm w-[120px]',
            disableYearOnly ? 'bg-amber-50 text-amber-800 border-amber-300' : '',
          ].join(' ')}
          value={y || ''}             // ★ 局所状態
          onChange={onYear}
          aria-label="Year"
          disabled={disabled || disableYearOnly}
        >
          <option value="">Year</option>
          {years.map((yy) => (
            <option key={yy} value={yy}>{yy}</option>
          ))}
        </select>

        {allowClear && (
          <button
            type="button"
            onClick={clearAll}
            className="ml-1 rounded border px-2 py-1 text-xs text-zinc-600 hover:bg-zinc-50 disabled:opacity-50"
            title="Clear"
            disabled={disabled}
          >
            Clear
          </button>
        )}
      </div>
    </div>
  );
}
